import { useInfiniteQuery, useIsFetching, useQuery } from '@tanstack/react-query';

import { nktvKeys } from '../chung/hooks/keys';
import type {
  TrangLuotGoi,
  TrangTrace,
  TruyVanLuong,
  TruyVanLuotGoi,
  TruyVanTrace,
  TuyChonLocAi,
} from '../chung/nghiep-vu/types-vet';
import { useNguonVet } from './nguon';

/**
 * Hook dữ liệu của màn Sử dụng AI — port `vet/hooks.ts` native. Kho và tiền tố khoá lấy từ `NguonVet` (`nguon.tsx`):
 * cổng tenant và cổng nền tảng, và từng tenant đang lọc, là những khoá khác nhau. Khoá đặt dưới gốc `nktv` để một lượt
 * `invalidateQueries({ queryKey: nktvKeys.all })` làm mới được cả nhóm.
 *
 * Tham số truyền vào khoá là **toàn bộ bộ lọc TRỪ con trỏ** — đó là cách hai danh sách reset phân trang: đổi một ô lọc
 * là đổi khoá, khoá mới là một `useInfiniteQuery` khác bắt đầu từ `initialPageParam`. Không cần `useEffect` nào.
 */
const GOC = [...nktvKeys.all, 'vet'] as const;

export const vetKeys = {
  all: GOC,
  trace: (nguon: readonly unknown[], thamSo: TruyVanTrace) => [...GOC, ...nguon, 'trace', thamSo] as const,
  chuoi: (nguon: readonly unknown[], traceId: string, tenantId: string | undefined) =>
    [...GOC, ...nguon, 'chuoi', traceId, tenantId ?? ''] as const,
  luotGoi: (nguon: readonly unknown[], thamSo: TruyVanLuotGoi) => [...GOC, ...nguon, 'luot-goi', thamSo] as const,
  luong: (nguon: readonly unknown[], thamSo: TruyVanLuong) => [...GOC, ...nguon, 'luong', thamSo] as const,
  boLoc: (nguon: readonly unknown[], thamSo: { tu: string; den: string }) =>
    [...GOC, ...nguon, 'bo-loc', thamSo] as const,
};

const CU_SAU_MS = 30_000;
const BO_LOC_CU_SAU_MS = 300_000;

/**
 * Danh sách chuỗi xử lý.
 *
 * ⚠️ `getNextPageParam` đọc `con_tro_tiep`, KHÔNG đọc `con_nua`. Ở chế độ `sap_xep='ton_nhat'` server trả `con_nua:
 * true` (còn trace ngoài bảng xếp hạng) nhưng `con_tro_tiep: null` vì keyset không dùng được trên khoá `tong_token`.
 */
export function useDanhSachTrace(thamSo: TruyVanTrace) {
  const { kho, khoa } = useNguonVet();
  return useInfiniteQuery({
    queryKey: vetKeys.trace(khoa, thamSo),
    queryFn: ({ pageParam }) => kho.trace(pageParam === undefined ? thamSo : { ...thamSo, con_tro: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (trang: TrangTrace) => trang.con_tro_tiep ?? undefined,
    staleTime: CU_SAU_MS,
  });
}

export function useDanhSachLuotGoi(thamSo: TruyVanLuotGoi) {
  const { kho, khoa } = useNguonVet();
  return useInfiniteQuery({
    queryKey: vetKeys.luotGoi(khoa, thamSo),
    queryFn: ({ pageParam }) => kho.luotGoi(pageParam === undefined ? thamSo : { ...thamSo, con_tro: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (trang: TrangLuotGoi) => trang.con_tro_tiep ?? undefined,
    staleTime: CU_SAU_MS,
  });
}

/**
 * Các bước của một chuỗi — chỉ gọi khi ngăn kéo waterfall mở (một chuỗi có thể tới 200 bước). `staleTime: Infinity`:
 * các bước của một trace đã xong thì không đổi nữa — nhật ký chỉ cho thêm. `tenantId` là tenant của dòng đã bấm (cổng
 * nền tảng cần nó).
 */
export function useChuoiBuoc(traceId: string | null, tenantId?: string) {
  const { kho, khoa } = useNguonVet();
  return useQuery({
    queryKey: vetKeys.chuoi(khoa, traceId ?? '', tenantId),
    queryFn: () => kho.chuoi(traceId as string, tenantId),
    enabled: traceId !== null && traceId !== '',
    staleTime: Infinity,
  });
}

/** Gom theo đối tượng nghiệp vụ. Không phân trang — server đã sắp theo token giảm dần. */
export function useLuong(thamSo: TruyVanLuong) {
  const { kho, khoa } = useNguonVet();
  return useQuery({
    queryKey: vetKeys.luong(khoa, thamSo),
    queryFn: () => kho.luong(thamSo),
    staleTime: CU_SAU_MS,
  });
}

/**
 * Danh mục giá trị cho các ô lọc, theo đúng khoảng đang xem. `retry: false`: hỏng thì ô lọc chỉ thiếu gợi ý, không
 * đáng ba lượt thử lại mỗi lần đổi khoảng. Khoá chỉ gồm hai mốc, nên đổi một ô lọc khác không gọi lại.
 */
export function useBoLocAi(tu: string, den: string) {
  const { kho, khoa } = useNguonVet();
  return useQuery<TuyChonLocAi>({
    queryKey: vetKeys.boLoc(khoa, { tu, den }),
    queryFn: () => kho.boLoc({ tu, den }),
    staleTime: BO_LOC_CU_SAU_MS,
    retry: false,
  });
}

/** Màn đang tải lại gì đó của nhóm `nktv` không — cho trạng thái của nút Làm mới. */
export function useDangTaiNktv(): boolean {
  return useIsFetching({ queryKey: nktvKeys.all }) > 0;
}
